import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./timeline.css";

gsap.registerPlugin(ScrollTrigger);

document.addEventListener("DOMContentLoaded", () => {
  const section = document.querySelector("[data-timeline]");
  if (!section) return;

  const track = section.querySelector(".timeline-items");
  const nav = section.querySelector("[data-timeline-nav]");
  const ruler = section.querySelector("[data-timeline-ruler]");
  const rulerLine = section.querySelector("[data-timeline-ruler-line]");
  const rulerGrid = section.querySelector("[data-timeline-ruler-grid]");
  const isNarrowViewport = window.matchMedia("(max-width: 1023px)");

  const getScrollWidth = () => track.scrollWidth - window.innerWidth;
  const getHeaderHeight = () => document.querySelector(".site-header").offsetHeight;

  const ticks = section.querySelectorAll("[data-timeline-tick]");
  const yearEls = section.querySelectorAll("[data-timeline-year]");
  const yearLabelEls = section.querySelectorAll("[data-timeline-year-label]");
  const slides = Array.from(section.querySelectorAll("[data-timeline-slide]"));
  const tickArr = Array.from(ticks);
  const firstYearIdx = tickArr.indexOf(yearEls[0]);
  const lastYearIdx = tickArr.indexOf(yearEls[yearEls.length - 1]);
  const yearRange = lastYearIdx - firstYearIdx;
  const scrollDuration = 1;
  const totalDuration = 1.05;
  const hasYearRange = yearRange > 0 && firstYearIdx >= 0 && lastYearIdx >= 0;
  const leadingTicks = firstYearIdx;
  const trailingTicks = ticks.length - 1 - lastYearIdx;
  const mobileMinStepPx = 12;
  const defaultRulerLineWidth = rulerLine?.style.width || "";
  const defaultRulerLineMarginLeft = rulerLine?.style.marginLeft || "";
  const defaultRulerGridAutoColumns = rulerGrid?.style.gridAutoColumns || "";
  const defaultRulerGridWidth = rulerGrid?.style.width || "";
  const defaultRulerGridMarginLeft = rulerGrid?.style.marginLeft || "";
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const animatedSlides = new Set();
  let mobileRulerStartX = 0;
  let mobileRulerEndX = 0;

  const getSlideTargets = (slide) => {
    const image = slide.querySelector("[data-timeline-slide-image]");
    const year = slide.querySelector("[data-timeline-slide-year]");
    const text = Array.from(slide.querySelectorAll("[data-timeline-slide-text]")).filter((el) => el !== year);
    return { image, year, text };
  };

  const animateSlideIn = (index) => {
    if (index < 0 || index >= slides.length || animatedSlides.has(index)) return;

    const slide = slides[index];
    const { image, year, text } = getSlideTargets(slide);
    animatedSlides.add(index);

    if (prefersReducedMotion) {
      if (year) {
        gsap.set(year, { autoAlpha: 1, x: 0, scale: 1, clearProps: "transform,opacity,visibility" });
      }
      if (text.length > 0) {
        gsap.set(text, { autoAlpha: 1, y: 0, clearProps: "transform,opacity,visibility" });
      }
      if (image) {
        gsap.set(image, { autoAlpha: 1, y: 0, scale: 1, clearProps: "transform,opacity,visibility" });
      }

      return;
    }

    if (year) {
      gsap.to(year, {
        autoAlpha: 1,
        x: 0,
        scale: 1,
        duration: 1.2,
        ease: "power4.out",
        overwrite: "auto",
      });
    }

    if (text.length > 0) {
      gsap.to(text, {
        autoAlpha: 1,
        y: 0,
        duration: 0.9,
        delay: 0.12,
        stagger: 0.25,
        ease: "power2.out",
        overwrite: "auto",
      });
    }

    if (image) {
      gsap.to(image, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.8,
        delay: 0.08,
        ease: "power2.out",
        overwrite: "auto",
      });
    }
  };

  const getActiveSlideIndex = () => {
    if (slides.length <= 1) return 0;

    const viewportCenterX = window.innerWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const slideCenterX = rect.left + rect.width / 2;
      const distanceToViewportCenter = Math.abs(slideCenterX - viewportCenterX);

      if (distanceToViewportCenter < closestDistance) {
        closestDistance = distanceToViewportCenter;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const applyRulerLayout = () => {
    if (!nav || !rulerLine || !rulerGrid || ticks.length === 0) return;

    if (isNarrowViewport.matches && hasYearRange) {
      // Keep desktop-like breathing room while centering years on narrow viewports:
      // leading/trailing tick groups should fill at least half the nav width.
      const minStepForLeadingCoverage = leadingTicks > 0 ? nav.clientWidth / (leadingTicks * 2) : 0;
      const minStepForTrailingCoverage = trailingTicks > 0 ? nav.clientWidth / (trailingTicks * 2) : 0;
      const stepPx = Math.max(
        nav.clientWidth / yearRange,
        mobileMinStepPx,
        minStepForLeadingCoverage,
        minStepForTrailingCoverage,
      );
      const railWidthPx = ticks.length * stepPx;
      const railOffsetPx = -firstYearIdx * stepPx;

      rulerLine.style.width = `${railWidthPx}px`;
      rulerLine.style.marginLeft = `${railOffsetPx}px`;

      rulerGrid.style.gridAutoColumns = `${stepPx}px`;
      rulerGrid.style.width = `${railWidthPx}px`;
      rulerGrid.style.marginLeft = `${railOffsetPx}px`;
    } else {
      rulerLine.style.width = defaultRulerLineWidth;
      rulerLine.style.marginLeft = defaultRulerLineMarginLeft;

      rulerGrid.style.gridAutoColumns = defaultRulerGridAutoColumns;
      rulerGrid.style.width = defaultRulerGridWidth;
      rulerGrid.style.marginLeft = defaultRulerGridMarginLeft;
    }
  };

  slides.forEach((slide) => {
    const { image, year, text } = getSlideTargets(slide);

    if (prefersReducedMotion) {
      if (year) gsap.set(year, { autoAlpha: 1, x: 0 });
      if (text.length > 0) gsap.set(text, { autoAlpha: 1, y: 0 });
      if (image) gsap.set(image, { autoAlpha: 1, y: 0, scale: 1 });
      return;
    }

    if (year) gsap.set(year, { autoAlpha: 0, x: 120, scale: 0.86 });
    if (text.length > 0) gsap.set(text, { autoAlpha: 0, y: 24 });
    if (image) gsap.set(image, { autoAlpha: 0, y: 28, scale: 0.98 });
  });

  const measureMobileRulerRange = () => {
    if (!nav || !ruler || yearEls.length === 0) return;

    // Measure from a stable baseline so previous transforms don't skew bounds.
    ruler.style.transform = "translate3d(0px, 0, 0)";

    const navRect = nav.getBoundingClientRect();
    const firstYearRect = yearEls[0].getBoundingClientRect();
    const lastYearRect = yearEls[yearEls.length - 1].getBoundingClientRect();
    const navCenterX = navRect.left + navRect.width / 2;
    const firstYearCenterX = firstYearRect.left + firstYearRect.width / 2;
    const lastYearCenterX = lastYearRect.left + lastYearRect.width / 2;

    mobileRulerStartX = navCenterX - firstYearCenterX;
    mobileRulerEndX = navCenterX - lastYearCenterX;
  };

  if (isNarrowViewport.addEventListener) {
    isNarrowViewport.addEventListener("change", () => ScrollTrigger.refresh());
  }

  const applyVisualState = (scrollProgress) => {
    if (ruler) {
      if (isNarrowViewport.matches) {
        const rulerX = hasYearRange
          ? mobileRulerStartX + (mobileRulerEndX - mobileRulerStartX) * scrollProgress
          : mobileRulerStartX;
        ruler.style.transform = `translate3d(${rulerX}px, 0, 0)`;
      } else {
        ruler.style.transform = "";
      }
    }

    ticks.forEach((tick, i) => {
      const raw = hasYearRange ? (i - firstYearIdx) / yearRange : i <= firstYearIdx ? 0 : 1;
      const tickProgress = Math.max(0, Math.min(1, raw));
      const bar = tick.querySelector("div") || tick;
      bar.style.opacity = scrollProgress >= tickProgress ? "1" : "0.4";
    });

    yearEls.forEach((el, i) => {
      const yearProgress = yearEls.length > 1 ? i / (yearEls.length - 1) : 0;
      const span = el.querySelector("span");
      span.style.opacity = scrollProgress >= yearProgress ? "1" : "0.4";
    });
  };

  ticks.forEach((tick) => {
    const bar = tick.querySelector("div") || tick;
    bar.style.transition = "opacity 0.3s ease";
  });

  yearEls.forEach((el) => {
    const span = el.querySelector("span");
    span.style.transition = "opacity 0.3s ease";
  });

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      pin: true,
      scrub: 1.5,
      // markers: true,
      start: () => `top ${getHeaderHeight()}`,
      end: () => `+=${getScrollWidth() + window.innerHeight}`,
      invalidateOnRefresh: true,
      onRefresh: (self) => {
        applyRulerLayout();
        measureMobileRulerRange();
        const scrollProgress = Math.min(self.progress / (scrollDuration / totalDuration), 1);
        applyVisualState(scrollProgress);
      },
      onUpdate: (self) => {
        const scrollProgress = Math.min(self.progress / (scrollDuration / totalDuration), 1);
        const activeSlideIdx = getActiveSlideIndex();

        animateSlideIn(activeSlideIdx);
        applyVisualState(scrollProgress);
      },
    },
  });

  tl.to(track, {
    x: () => -getScrollWidth(),
    ease: "none",
    duration: 1,
  });

  // Hold on last slide
  tl.to({}, { duration: 0.05 });

  const jumpToYear = (index) => {
    const st = tl.scrollTrigger;
    if (!st) return;

    const yearProgress = yearEls.length > 1 ? index / (yearEls.length - 1) : 0;
    const timelineProgress = yearProgress * (scrollDuration / totalDuration);
    const baseTargetScroll = st.start + (st.end - st.start) * timelineProgress;
    const activationNudgePx = Math.max(16, (st.end - st.start) * 0.002);
    const targetScroll = Math.min(baseTargetScroll + activationNudgePx, st.end);

    window.scrollTo({
      top: targetScroll,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  yearLabelEls.forEach((label, index) => {
    label.addEventListener("click", () => jumpToYear(index));
    label.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        jumpToYear(index);
      }
    });
  });

  // Set initial state before pinning starts.
  applyRulerLayout();
  measureMobileRulerRange();
  animateSlideIn(0);
  applyVisualState(0);
});
